import React, { useContext, useEffect } from "react";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import _ from "lodash";
import jssVariables from "../../../../jss_setup";
import {
  CurrencyContext,
  TranslationContext,
  currencies,
  getAgeFromTimestamp,
} from "../../../../umbilical_tools";
import { DateTime } from "luxon";
import { InlineIcon } from "@iconify/react";
import tinycolor from "tinycolor2";
import { prebookUpdateFerries } from "../../../../api/ferries";
import { getExchangedPrice } from "../../../../umbilical_hocs";
import { useSelector } from "react-redux";
import { PrebookUpdateLoader } from "./components";
import TripInfo from "../../../../pages/Ferries/TripInfo";
import { toast } from "react-toastify";
import { ferriesErrorMsgs } from "../../../../errors";
import { CustomCheckbox } from "../../../forms/inputGroup/helpers";

const titlesMap = { DR: "Dr.", MR: "Mr.", MRS: "Mrs.", MS: "Ms." };
const tripInfoStyles = {
  TripInfo: {
    smallscreen: {
      gridColumn: "unset",
      justifyContent: "center",
    },
  },
};

const grayColor = "#b2b2b2";

const cancellationPolicyStyles = createUseStyles({
  CancellationPolicy: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    borderRadius: jssVariables.half_gap,
    border: "1px solid #b2b2b2",
    borderColor: jssVariables.colors.base_color,
    padding: [jssVariables.double_gap, 0],
    paddingRight: `calc(${jssVariables.normal_gap} * 4)`,
    position: "relative",
    ..._.get(props, "customStyles.CancellationPolicy", {}),
  }),
  ul: (props) => ({
    "& li::marker": {
      color: jssVariables.colors.base_color,
      fontSize: "small",
    },
    margin: 0,
    ..._.get(props, "customStyles.ul", {}),
  }),
  infoIcon: (props) => ({
    position: "absolute",
    top: jssVariables.half_gap,
    right: jssVariables.half_gap,
    color: jssVariables.colors.base_color,
    fontSize: "x-large",
    ..._.get(props, "customStyles.infoIcon", {}),
  }),
  free: (props) => ({
    color: "green",
    ..._.get(props, "customStyles.free", {}),
  }),
  nonRefundable: (props) => ({
    paddingLeft: jssVariables.double_gap,
    color: "red",
    ..._.get(props, "customStyles.nonRefundable", {}),
  }),
  chargeable: (props) => ({
    color: "red",
    ..._.get(props, "customStyles.chargeable", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {},
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
export const CancellationPolicy = ({ cxl_policies, currency }) => {
  const classes = cancellationPolicyStyles({});
  const { t } = useContext(TranslationContext);
  const { currency: settedCurrency } = useContext(CurrencyContext);

  // const cxlMessages = [
  //   ...new Set(
  //     _.flatten(
  //       legs.map((leg) =>
  //         _.get(leg, "ticket_type.sales_messages", []).map((message) => message)
  //       )
  //     )
  //   ),
  // ];

  return (
    <div className={classes.CancellationPolicy}>
      <InlineIcon icon="mdi:alert-circle" className={classes.infoIcon} />
      <div className={classes.cxls}>
        {cxl_policies.map((cxl, idx) =>
          cxl.frame_type === "non-refundable" ? (
            <span className={classes.nonRefundable} key={idx}>
              {t("thisBookingIsNonNefundable")}.
            </span>
          ) : (
            <span key={idx}>
              <ul className={classes.ul}>
                <li>
                  {cxl.frame_type === "refundable"
                    ? `Up ${t("to")} `
                    : `${t("from")}
                  ${DateTime.fromISO(cxl.date_from).toLocaleString(
                    DateTime.DATETIME_SHORT
                  )} ${t("to")} `}
                  {DateTime.fromISO(cxl.date_to).toLocaleString(
                    DateTime.DATETIME_SHORT
                  )}{" "}
                  <span
                    className={
                      cxl.frame_type === "refundable"
                        ? classes.free
                        : classes.chargeable
                    }
                    key={idx}>
                    {t("cancelationIs")}{" "}
                    {cxl.frame_type === "refundable" ? (
                      t("free")
                    ) : (
                      <span>
                        {`${t("chargeableWithFeeOf")} `}
                        {currencies[settedCurrency]}
                        {getExchangedPrice({
                          price: cxl.fee,
                          priceCurrency: currency,
                          settedCurrency,
                          withDecimals: true,
                        })}
                      </span>
                    )}{" "}
                  </span>
                </li>
              </ul>
            </span>
          )
        )}
      </div>
    </div>
  );
};
CancellationPolicy.defaultProps = {
  cxl_policies: [],
  currency: "",
};
CancellationPolicy.propTypes = {
  cxl_policies: PropTypes.array,
  currency: PropTypes.string,
};

const prebookUpdateStyles = createUseStyles({
  PrebookUpdate: (props) => ({
    boxShadow: jssVariables.shadows.normalShadow,
    padding: jssVariables.double_gap,
    borderRadius: jssVariables.half_gap,
    maxHeight: "70vh",
    overflowY: "auto",
    ..._.get(props, "customStyles.PrebookUpdate", {}),
  }),
  header: (props) => ({
    background: jssVariables.colors.base_color,
    color: jssVariables.colors.text.light,
    fontSize: "x-large",
    fontWeight: 500,
    textTransform: "capitalize",
    padding: [jssVariables.half_gap, jssVariables.normal_gap],
    borderRadius: jssVariables.half_gap,
    ..._.get(props, "customStyles.header", {}),
  }),
  content: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    padding: jssVariables.normal_gap,
    fontSize: "large",
    ..._.get(props, "customStyles.content", {}),
  }),
  route: (props) => ({
    fontSize: "x-large",
    fontWeight: 500,
    textTransform: "capitalize",
    ..._.get(props, "customStyles.route", {}),
  }),
  info: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    gridGap: `${jssVariables.half_gap} calc(${jssVariables.normal_gap} * 3)`,
    ..._.get(props, "customStyles.info", {}),
  }),
  infoTitle: (props) => ({
    fontWeight: 500,
    textTransform: "capitalize",
    ..._.get(props, "customStyles.infoTitle", {}),
  }),
  passengers: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.passengers", {}),
  }),
  paxIcon: (props) => ({
    fontSize: "x-large",
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.paxIcon", {}),
  }),
  grayFontColor: (props) => ({
    fontSize: "large",
    textTransform: "capitalize",
    color: grayColor,
    ..._.get(props, "customStyles.grayFontColor", {}),
  }),
  dividerBaseColor: (props) => ({
    height: "1px",
    background: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.dividerBaseColor", {}),
  }),
  dividerGrayColor: (props) => ({
    height: "1px",
    background: grayColor,
    ..._.get(props, "customStyles.dividerBaseColor", {}),
  }),
  priceDetails: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    background: tinycolor(jssVariables.colors.base_color)
      .lighten(48)
      .toRgbString(),
    borderRadius: jssVariables.half_gap,
    padding: jssVariables.normal_gap,
    color: grayColor,
    ..._.get(props, "customStyles.priceDetails", {}),
  }),
  seatSelectionsTitle: (props) => ({
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.seatSelectionsTitle", {}),
  }),
  numOfSeats: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.numOfSeats", {}),
  }),
  totalPrice: (props) => ({
    justifySelf: "end",
    ..._.get(props, "customStyles.totalPrice", {}),
  }),
  totalPriceValue: (props) => ({
    fontSize: "x-large",
    fontWeight: "bold",
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.totalPriceValue", {}),
  }),
  priceExtraInfo: (props) => ({
    justifySelf: "end",
    ..._.get(props, "customStyles.priceExtraInfo", {}),
  }),
});
export const PrebookUpdate = ({
  travellers,
  selectionData,
  loadingData,
  setLoadingData,
  prebookUpdateData,
  setPrebookUpdateData,
  setStep,
  agreed,
  setAgreed,
}) => {
  const classes = prebookUpdateStyles();

  const { search_session_id, language, currency } = useSelector((state) => ({
    search_session_id: state.SessionIds.FER.search_session_id,
    language: state.FerrySearchForm.searchPayload.language,
    currency: state.FerrySearchForm.searchPayload.currency,
  }));

  const { t } = useContext(TranslationContext);
  const { currency: settedCurrency } = useContext(CurrencyContext);

  async function prebookUpdate(data) {
    const response = await prebookUpdateFerries({
      payload: data,
      onError: (error) => ({
        error: {
          status: error.response.status,
          message: error.response.data.message,
        },
      }),
    });
    if (response.error) {
      toast.error(ferriesErrorMsgs.general.unexpectedError);
      setStep(0);
    } else {
      setPrebookUpdateData(response);
    }
    setLoadingData(false);
  }

  function clearPaxForAPI(passengers) {
    function cleanData(group) {
      return group.map((p) => ({
        ...p,
        age: getAgeFromTimestamp(p.birthday),
        birthday: p.birthday?.[0] || "",
        ...(p.passport_start_date
          ? { passport_start_date: p.passport_start_date?.[0] || "" }
          : {}),
        ...(p.passport_expiry_date
          ? { passport_expiry_date: p.passport_expiry_date?.[0] || "" }
          : {}),
        ...(p.passport_start_date
          ? { passport_start_date: p.passport_start_date?.[0] }
          : {}),
      }));
    }

    return [...cleanData(passengers.adults), ...cleanData(passengers.children)];
  }

  // Handle loading state at mount.
  useEffect(() => {
    setLoadingData(true);
  }, []);

  // Execute prebook
  useEffect(() => {
    if (!loadingData) return;

    prebookUpdate({
      session_id: search_session_id,
      provider: selectionData.provider,
      token: selectionData.token,
      passengers: clearPaxForAPI(travellers),
      legs: (selectionData?.selections ?? []).map((sl) => ({
        is_return_leg: sl.is_return_leg,
        accommodations: [{ id: sl.id, quantity: sl.tickets }],
      })),
    });
  }, [loadingData]);

  const legs = prebookUpdateData?.legs ?? [];
  return (
    <div className={classes.PrebookUpdate}>
      {loadingData || _.isEmpty(prebookUpdateData) ? (
        <PrebookUpdateLoader />
      ) : (
        <React.Fragment>
          <div className={classes.header}>{t("summary")}</div>
          <div className={classes.content}>
            <div className={classes.route}>
              {legs?.[0]?.departure_port_name ?? ""}
              {` ${t("to")} `}
              {legs?.[0]?.arrival_port_name ?? ""}
              {(legs?.[1]?.is_return_leg ?? false) && `, ${t("return")}`}
            </div>
            <div className={classes.dates}>
              {DateTime.fromISO(legs?.[0]?.departure_datetime ?? "")
                .setZone("UTC", { keepLocalTime: false })
                .toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY)}
              {(legs?.[1]?.is_return_leg ?? false) &&
                ` - ${DateTime.fromISO(legs?.[1]?.departure_datetime ?? "")
                  .setZone("UTC", { keepLocalTime: false })
                  .toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY)}`}
            </div>
            <div className={classes.info}>
              <div className={classes.infoTitle}>{t("passengers")}</div>
              <div className={classes.passengers}>
                {(prebookUpdateData?.detailed_passengers ?? []).map(
                  (pax, idx) => (
                    <div className={classes.passenger} key={idx}>
                      <InlineIcon
                        icon="ic:baseline-person"
                        className={classes.paxIcon}
                      />
                      {` ${titlesMap[pax.title] ? titlesMap[pax.title] : ""} ${
                        pax.first_name
                      } ${pax.last_name} `}
                      <span className={classes.grayFontColor}>
                        {`(${pax.age_category})`}
                        {pax.is_leader && ` (${t("leader")})`}
                      </span>
                    </div>
                  )
                )}
              </div>
              {/* <div className={classes.infoTitle}>{t("delivery")}</div>
              {"Mobile ticket__"} */}
            </div>
            <div className={classes.dividerBaseColor} />
            {prebookUpdateData.legs.map((leg, idx) => (
              <TripInfo
                key={idx}
                leg={leg}
                operatorIcon={leg.operator.logo}
                extraStyles={tripInfoStyles}
              />
            ))}
            <div className={classes.dividerBaseColor} />
            <div className={classes.priceDetails}>
              {prebookUpdateData.legs.map((leg, idx) => (
                <div className={classes.seatSelections} key={idx}>
                  <span className={classes.seatSelectionsTitle}>
                    {`${t(idx === 0 ? "outbound" : "inbound")}
                  (${leg.route_name})`}
                  </span>
                  {leg.accommodations.map((acc, index) => (
                    <div className={classes.numOfSeats} key={index}>
                      <span>{acc.description.split("|")[0]}</span>

                      <strong>{` x ${acc.quantity}`}</strong>
                    </div>
                  ))}
                </div>
              ))}
              <div className={classes.dividerGrayColor} />
              <div className={classes.totalPrice}>
                {t("total")}{" "}
                <span className={classes.totalPriceValue}>
                  {getExchangedPrice({
                    price: prebookUpdateData.price.value,
                    priceCurrency: prebookUpdateData.price.currency,
                    settedCurrency,
                  }).toLocaleString("en-US", {
                    style: "currency",
                    currency: currency,
                  })}
                </span>
              </div>
              <div className={classes.priceExtraInfo}>
                {t("currencyForTickets")}{" "}
                {language &&
                  new Intl.DisplayNames([language], { type: "currency" }).of(
                    currency
                  )}{" "}
                ({currency})
              </div>
            </div>
            <CancellationPolicy
              cxl_policies={prebookUpdateData.cancellation_policies}
              currency={prebookUpdateData.price.currency}
            />
            <CustomCheckbox
              label={t("iHaveReadAgreeWithAbove")}
              checked={agreed}
              setChecked={setAgreed}
            />
          </div>
        </React.Fragment>
      )}
    </div>
  );
};
PrebookUpdate.defaultProps = {
  selectionData: {},
  travellers: {},
  prebookUpdateData: {},
  loadingData: true,
};
PrebookUpdate.propTypes = {
  selectionData: PropTypes.object,
  travellers: PropTypes.object,
  loadingData: PropTypes.bool.isRequired,
  prebookUpdateData: PropTypes.object,
  setPrebookUpdateData: PropTypes.func,
  setLoadingData: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
  agreed: PropTypes.bool.isRequired,
  setAgreed: PropTypes.func.isRequired,
};
